import { Suspense, useEffect, useRef } from 'react';
import { Box, Link } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation } from 'react-router';
import { MobileHeader } from './MobileHeader';
import { MobileNav } from './MobileNav';
import { PageHeader } from './PageHeader';
import { SHELL_GAP, scrollInsetSx } from './shellGeometry';
import { SideNav } from './SideNav';

const SkipLink = () => {
  const { t } = useTranslation();
  return (
    <Link
      href="#main-content"
      sx={{
        position: 'absolute',
        left: '-9999px',
        zIndex: 'tooltip',
        '&:focus': {
          position: 'fixed',
          top: 8,
          left: 8,
          zIndex: 'tooltip',
          bgcolor: 'background.paper',
          px: 2,
          py: 1,
          borderRadius: 1,
          boxShadow: 3,
        },
      }}
    >
      {t('SkipToContent')}
    </Link>
  );
};

/** Active-phase shell: floating dock (desktop) / header + bottom nav (mobile).
 *  One tree across the tiers — the Outlet keeps its position, so crossing a
 *  breakpoint never remounts the routed page. The body paints the background. */
export const Layout = () => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  // Narrow-desktop tier: the dock collapses to a hover-expanding icon rail.
  const isFullDesktop = useMediaQuery(theme.breakpoints.up('xl'));

  const { pathname } = useLocation();
  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0, behavior: 'instant' });
  }, [pathname]);

  return (
    <Box
      sx={{
        height: '100dvh',
        display: 'flex',
        flexDirection: isDesktop ? 'row' : 'column',
        ...(isDesktop && {
          p: `${String(SHELL_GAP)}px`,
          position: 'relative',
        }),
      }}
    >
      <SkipLink />
      {isDesktop ? <SideNav rail={!isFullDesktop} /> : <MobileHeader />}
      <Box
        component="main"
        sx={{
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {isDesktop && <PageHeader />}
        {/* The skip link lands on the scroll container itself, so keyboard
            scrolling works right after skipping. */}
        <Box
          id="main-content"
          ref={scrollRef}
          tabIndex={-1}
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            ...scrollInsetSx,
            '&:focus': { outline: 'none' },
          }}
        >
          <Suspense>
            <Outlet />
          </Suspense>
        </Box>
      </Box>
      {!isDesktop && <MobileNav />}
    </Box>
  );
};
